import { createSlice } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { Microservices } from "../../app/AllowedMicroservices";
import { RootState } from "../../app/store";
import apiActionCreator, { HttpMethods } from "../../services/apiActionCreator";
import { FetchStatus } from "../common/enums";

// Mirrors PUT /progress-audit/progress/:progressId/course-exam/:evaluationId/fix
// (service-learning, SuperAdmin only) - the manual correction for a
// course's final exam the student actually passed (confirmed against
// service-evaluation's EvaluationResult) but that never made it into
// Progress.evaluationsPassed (SYSTEM.debt.md ítem 29/§15.29). Same pattern
// as progressAuditFixTopicEvaluationSlice, but scoped to the course-level
// exam instead of a topic evaluation.
const DEFAULT_ERROR_MESSAGE = "No se pudo corregir el examen final del curso";

interface ProgressAuditFixCourseExamState {
  status: FetchStatus;
}

const initialState: ProgressAuditFixCourseExamState = {
  status: FetchStatus.Idle,
};

export const progressAuditFixCourseExamSlice = createSlice({
  name: "progressAuditFixCourseExam",
  initialState,
  reducers: {
    fixing: (state) => {
      state.status = FetchStatus.Fetching;
    },
    fixed: (state) => {
      state.status = FetchStatus.Fetched;
    },
    error: (state) => {
      state.status = FetchStatus.Error;
    },
  },
});

export const { fixing, fixed, error } = progressAuditFixCourseExamSlice.actions;

export const fixProgressAuditCourseExam = (
  progressId: string,
  evaluationId: string,
  onSuccess: () => void = () => {},
  onError: (message: string) => void = () => {}
) => (dispatch: Dispatch<object>) => {
  return dispatch(
    apiActionCreator({
      endpoint: `/progress-audit/progress/${progressId}/course-exam/${evaluationId}/fix`,
      types: {
        requestType: fixing,
        successTypes: [
          {
            actionOrCreator: fixed,
            selector: () => {
              onSuccess();
            },
          },
        ],
        errorTypes: [
          {
            actionOrCreator: error,
            selector: (response: { data?: { data?: string } }) => {
              onError(response?.data?.data || DEFAULT_ERROR_MESSAGE);
            },
          },
        ],
      },
      method: HttpMethods.PUT,
      microservice: Microservices.LEARNING_SERVICE,
      authenticated: true,
    })
  );
};

export const selectIsFixingProgressAuditCourseExam = (state: RootState) =>
  state.progressAuditFixCourseExam.status === FetchStatus.Fetching;

export default progressAuditFixCourseExamSlice.reducer;
